import React, { useState } from 'react';
import { User, Mail, Phone, Shield, Key, CheckCircle, Leaf, Stethoscope, Award, Save, ShieldCheck } from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import { Card, Button, Input, Badge, DoshaTag, LicensedDoctorBadge, DoctorLicenseModal } from '../../components/common/UI';
import { useAuth } from '../../context/AuthContext';
import { authAPI, usersAPI } from '../../services/api';
import toast from 'react-hot-toast';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const role = user?.role || 'patient';
  const [showLicenseModal, setShowLicenseModal] = useState(false);

  // Navigation items per role
  const navItemsByRole = {
    patient: [
      { path: '/patient/dashboard', label: 'Dashboard', icon: Leaf },
      { path: '/patient/appointments', label: 'Appointments', icon: User },
      { path: '/patient/profile', label: 'My Profile', icon: User },
    ],
    doctor: [
      { path: '/doctor/dashboard', label: 'Dashboard', icon: Leaf },
      { path: '/doctor/appointments', label: 'Appointments', icon: User },
      { path: '/doctor/profile', label: 'My Profile', icon: User },
    ],
    admin: [
      { path: '/admin/dashboard', label: 'Dashboard', icon: Leaf },
      { path: '/admin/users', label: 'Manage Users', icon: User },
      { path: '/admin/profile', label: 'My Profile', icon: User },
    ],
  };

  const navItems = navItemsByRole[role] || navItemsByRole.patient;

  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    dosha: user?.patientProfile?.dosha || 'Vata',
    specialization: user?.doctorProfile?.specialization || '',
    experience: user?.doctorProfile?.experience || '',
    licenseNumber: user?.doctorProfile?.licenseNumber || '',
    medicalCouncil: user?.doctorProfile?.medicalCouncil || 'Tamil Nadu Board of Indian Medicine',
    licenseState: user?.doctorProfile?.licenseState || 'Tamil Nadu',
  });
  const [updating, setUpdating] = useState(false);

  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [updatingPw, setUpdatingPw] = useState(false);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name cannot be empty');
    setUpdating(true);
    try {
      const updateData = { name: form.name, phone: form.phone };
      if (role === 'patient') {
        updateData.patientProfile = { ...user?.patientProfile, dosha: form.dosha };
      } else      if (role === 'doctor') {
        const isNewLicense = form.licenseNumber !== user?.doctorProfile?.licenseNumber;
        updateData.doctorProfile = {
          ...user?.doctorProfile,
          specialization: form.specialization,
          experience: Number(form.experience) || user?.doctorProfile?.experience,
          licenseNumber: form.licenseNumber,
          medicalCouncil: form.medicalCouncil,
          licenseState: form.licenseState,
          licenseStatus: isNewLicense ? 'pending' : (user?.doctorProfile?.licenseStatus || 'approved')
        };
      }

      const res = await usersAPI.update(user._id, updateData);
      const updated = res.data.user || { ...user, ...updateData };
      updateUser(updated);
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.currentPassword || !pwForm.newPassword) {
      return toast.error('Please enter current and new password');
    }
    if (pwForm.newPassword.length < 6) {
      return toast.error('New password must be at least 6 characters');
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      return toast.error('New passwords do not match');
    }
    setUpdatingPw(true);
    try {
      await authAPI.changePassword({
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      toast.success('Password changed successfully!');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Current password incorrect');
    } finally {
      setUpdatingPw(false);
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'AC';

  const themeGradients = {
    patient: 'from-emerald-600 to-teal-700',
    doctor: 'from-blue-600 to-indigo-700',
    admin: 'from-violet-600 to-purple-700',
  };
  const grad = themeGradients[role] || themeGradients.patient;

  return (
    <DashboardLayout navItems={navItems} role={role}>
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Profile Hero Header */}
        <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-r ${grad} p-6 sm:p-8 text-white shadow-xl`}>
          <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-white/10 rounded-full blur-xl pointer-events-none" />
          <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white text-2xl font-extrabold border border-white/30 shadow-inner flex-shrink-0">
              {initials}
            </div>
            <div className="text-center sm:text-left flex-1 min-w-0">
              <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold truncate">{user?.name}</h1>
                <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-white/20 text-white backdrop-blur-sm border border-white/20">
                  {role}
                </span>
                {role === 'doctor' && <LicensedDoctorBadge doctor={user} onClick={() => setShowLicenseModal(true)} />}
              </div>
              <p className="text-white/80 text-sm flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                <Mail className="w-4 h-4" /> {user?.email}
                {user?.phone && <><span className="opacity-50">•</span><Phone className="w-4 h-4" /> {user.phone}</>}
              </p>
              <p className="text-white/60 text-xs mt-2">
                Member since {new Date(user?.createdAt || Date.now()).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Main Edit Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card title="Account Details" subtitle="Manage your personal profile information">
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your full name" required />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <Input value={form.email} disabled className="bg-gray-100 text-gray-500 cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91-XXXXXXXXXX" />
                  </div>
                </div>

                {role === 'patient' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Prakriti / Dosha Constitution</label>
                    <div className="flex items-center gap-2">
                      <select value={form.dosha} onChange={e => setForm(f => ({ ...f, dosha: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-500 bg-gray-50">
                        <option value="Vata">Vata (Air &amp; Space)</option>
                        <option value="Pitta">Pitta (Fire &amp; Water)</option>
                        <option value="Kapha">Kapha (Earth &amp; Water)</option>
                        <option value="Tridosha">Tridosha (Balanced)</option>
                      </select>
                      <DoshaTag dosha={form.dosha} />
                    </div>
                  </div>
                )}

                {role === 'doctor' && (
                  <div className="space-y-4 pt-2 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                        <ShieldCheck className="w-4 h-4 text-emerald-600" /> Digital Medical Licensing &amp; Credentials
                      </h4>
                      <Button size="sm" variant="outline" onClick={() => setShowLicenseModal(true)}>
                        Preview Digital License Card
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Specialization</label>
                        <Input value={form.specialization} onChange={e => setForm(f => ({ ...f, specialization: e.target.value }))} placeholder="e.g. Panchakarma Specialist" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Experience (Years)</label>
                        <Input type="number" value={form.experience} onChange={e => setForm(f => ({ ...f, experience: e.target.value }))} placeholder="e.g. 10" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Medical License Number *</label>
                        <Input value={form.licenseNumber} onChange={e => setForm(f => ({ ...f, licenseNumber: e.target.value }))} placeholder="e.g. TN-AYU-2012-1234" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Medical Council / Board</label>
                        <Input value={form.medicalCouncil} onChange={e => setForm(f => ({ ...f, medicalCouncil: e.target.value }))} placeholder="e.g. Tamil Nadu Board of Indian Medicine" />
                      </div>
                    </div>
                  </div>
                )})}

                <div className="pt-2 flex justify-end">
                  <Button type="submit" loading={updating} className="flex items-center gap-2">
                    <Save className="w-4 h-4" /> Save Profile
                  </Button>
                </div>
              </form>
            </Card>

            {/* Password Change Card */}
            <Card title="Security & Password" subtitle="Update your password to keep your account secure">
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <Input type="password" value={pwForm.currentPassword} onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))} placeholder="••••••••" required />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <Input type="password" value={pwForm.newPassword} onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="Min 6 characters" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                    <Input type="password" value={pwForm.confirmPassword} onChange={e => setPwForm(f => ({ ...f, confirmPassword: e.target.value }))} placeholder="Repeat new password" required />
                  </div>
                </div>
                <div className="pt-2 flex justify-end">
                  <Button type="submit" variant="secondary" loading={updatingPw} className="flex items-center gap-2">
                    <Key className="w-4 h-4" /> Update Password
                  </Button>
                </div>
              </form>
            </Card>
          </div>

          {/* Sidebar Overview Card */}
          <div className="space-y-6">
            <Card title="Account Overview">
              <div className="space-y-4 text-sm">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500 flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-500" /> Account Status</span>
                  <Badge color="emerald">Active</Badge>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500 flex items-center gap-2"><User className="w-4 h-4 text-blue-500" /> Role Type</span>
                  <span className="font-semibold text-gray-700 capitalize">{role}</span>
                </div>
                {role === 'doctor' && user?.doctorProfile?.licenseNumber && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500 flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" /> License #</span>
                    <span className="font-mono text-xs font-semibold text-gray-700">{user.doctorProfile.licenseNumber}</span>
                  </div>
                )}
                {role === 'patient' && user?.patientProfile?.bloodGroup && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500 flex items-center gap-2"><Stethoscope className="w-4 h-4 text-rose-500" /> Blood Group</span>
                    <Badge color="red">{user.patientProfile.bloodGroup}</Badge>
                  </div>
                )}
                <div className="pt-2 text-xs text-gray-400 leading-relaxed">
                  🔒 Your personal data is protected under HIPAA &amp; Avartana Care data privacy protocols.
                </div>
              </div>
            </Card>
          </div>

        </div>
      </div>
      <DoctorLicenseModal isOpen={showLicenseModal} onClose={() => setShowLicenseModal(false)} doctor={user} />
    </DashboardLayout>
  );
}
